// Company-scoped reference lists (advisors, event types, locations) shared across pages.
//
// Kept in memory only (Vue.observable), never in Vuex, so they are not persisted to
// localStorage and can't go stale across company switches or sessions. Pages call
// loadLookups(...) on mount to refetch what they need; concurrent requests for the
// same list are shared, and switching company clears the lists and reloads them.
import Vue from 'vue'
import store from '@/data/vuex'

const ENDPOINTS = {
    advisors:    (company_id) => `admin/users/get_users_by_company/${company_id}`,
    event_types: (company_id) => `admin/event_types/get_event_types/${company_id}`,
    locations:   (company_id) => `admin/locations/get_locations/${company_id}`,
}

export const lookups = Vue.observable({
    company_id: null,
    advisors: [],
    event_types: [],
    locations: [],
})

const in_flight = {}
const requested = new Set()

function selectedCompanyId() {
    return store.getters['auth/get_selected_company']?.id || null
}

function resetFor(company_id) {
    lookups.company_id = company_id
    Object.keys(ENDPOINTS).forEach((key) => { lookups[key] = [] })
}

function fetchList(key, company_id) {
    const request_key = `${key}:${company_id}`
    if (!in_flight[request_key]) {
        in_flight[request_key] = Vue.axios.get(ENDPOINTS[key](company_id))
            .then((response) => {
                // Ignore late responses for a company that is no longer selected
                if (lookups.company_id !== company_id) return
                lookups[key] = response?.data?.response == true ? response.data.data : []
            })
            .finally(() => { delete in_flight[request_key] })
    }
    return in_flight[request_key]
}

// Fetch the given lists (default: all) for the selected company. Always refetches.
export function loadLookups(...keys) {
    const company_id = selectedCompanyId()
    if (!company_id) return Promise.resolve()
    if (lookups.company_id !== company_id) resetFor(company_id)

    const wanted = keys.length ? keys : Object.keys(ENDPOINTS)
    wanted.forEach((key) => requested.add(key))
    return Promise.all(wanted.map((key) => fetchList(key, company_id)))
}

export function advisorById(advisor_id) {
    return lookups.advisors.find((advisor) => advisor.id == advisor_id)
}

// Company switched while a page is open: drop the old company's lists and reload
// whatever pages have asked for so far.
store.watch(
    (state, getters) => getters['auth/get_selected_company']?.id,
    (company_id) => {
        if (!company_id || company_id === lookups.company_id) return
        resetFor(company_id)
        if (requested.size) loadLookups(...requested)
    }
)
